import * as swcHelpers from '@swc/helpers';
import { WebAuth } from 'auth0-js';
import { channel, buffers } from 'redux-saga';
import '@grandvisionhq/utils';

let webAuth;
let authOptions;
const dbConnection = 'Username-Password-Authentication';
/**
 * Some assertions that provide a clear error message when you've forgot to call init before other operations
 */ const assertInit = {
    isInitialized () {
        if (!webAuth) {
            return new Error("WebAuth hasn't been initialized. Make sure you call `init` before any other operations.");
        }
    },
    throwIfUninitialized () {
        const error = assertInit.isInitialized();
        if (error !== undefined) {
            throw error;
        }
    },
    rejectIfUninitialized (callback) {
        return swcHelpers.asyncToGenerator(function*() {
            const error = assertInit.isInitialized();
            if (error) {
                return Promise.reject(error);
            }
            return new Promise(callback);
        })();
    }
};
/**
 * If the token is about to expire a RenewalRequired action will be published on this channel.
 * Refreshing the token is up to the consumer. A call to `checkSession` will get a fresh token.
 */ const renewalRequired = channel(buffers.none());
let renewalTimeoutToken;
function scheduleRenewal(expiresIn) {
    window.clearTimeout(renewalTimeoutToken);
    renewalTimeoutToken = window.setTimeout(()=>{
        renewalRequired.put({
            type: '@@auth/RENEWAL_REQUIRED'
        });
    }, expiresIn * 1000 - 5000) // Request renewal 5s before it expires
    ;
}
/**
 * Creates a new WebAuth instance. Should be called at least once
 */ function init(options) {
    authOptions = options;
    webAuth = new WebAuth(authOptions);
}
/**
 * Wraps WebAuth.authorize, see: https://auth0.github.io/auth0.js/WebAuth.html#authorize
 * Redirects out to Auth0. For a nicer UX consumers should first try to resume a session by calling `checkSession`
 */ function authorize(options) {
    assertInit.throwIfUninitialized();
    webAuth.authorize(options);
}
/**
 * Wraps WebAuth.checkSession, see: https://auth0.github.io/auth0.js/WebAuth.html#checkSession
 *
 * It can be used to keep a session alive or continue on open one (say, after a page refresh)
 * Will also schedule a renewal event, see the `renewalRequired` channel at the top of the module
 */ function checkSession() {
    return _checkSession.apply(this, arguments);
}
function _checkSession() {
    _checkSession = swcHelpers.asyncToGenerator(function*(options = {}) {
        return assertInit.rejectIfUninitialized((resolve, reject)=>{
            webAuth.checkSession(options, (err, authResult)=>{
                if (err) {
                    reject(err);
                    return;
                }
                if (!authResult) {
                    reject(new Error(`Failed to checkSession, authResult was null`));
                    return;
                }
                if (authResult.expiresIn) {
                    scheduleRenewal(authResult.expiresIn);
                }
                resolve(authResult);
            });
        });
    });
    return _checkSession.apply(this, arguments);
}
/**
 * Wraps webAuth.crossOriginAuthentication, see: https://auth0.github.io/auth0.js/WebAuth.html#login
 * (search for cross-origin-authentication to find the correct type of login)
 *
 * Logs in the user with username and password using the cross origin authentication flow
 */ function crossOriginAuthentication(email, password) {
    return _crossOriginAuthentication.apply(this, arguments);
}
function _crossOriginAuthentication() {
    _crossOriginAuthentication = swcHelpers.asyncToGenerator(function*(email, password, options = {}) {
        return assertInit.rejectIfUninitialized((resolve, reject)=>{
            webAuth.login(swcHelpers.objectSpread({}, options, {
                username: email,
                realm: dbConnection,
                password,
                // @ts-ignore (types of auth0-js package are not complete
                responseType: 'token id_token',
                state: JSON.stringify({
                    email
                })
            }), (err, result)=>{
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    });
    return _crossOriginAuthentication.apply(this, arguments);
}
/**
 * Wraps WebAuth.parseHash, see: https://auth0.github.io/auth0.js/WebAuth.html#parseHash
 *
 * This function should be called on the returnUri (ie, the page that auth0 sends the user back to)
 * Will also schedule a renewal event, see the `renewalRequired` channel at the top of the module
 */ function parseHash(options) {
    return _parseHash.apply(this, arguments);
}
function _parseHash() {
    _parseHash = swcHelpers.asyncToGenerator(function*(options) {
        return assertInit.rejectIfUninitialized((resolve, reject)=>{
            const callback = (err, authResult)=>{
                if (err) {
                    reject(err);
                    return;
                }
                if (!authResult) {
                    reject(new Error(`Failed to parseHash, authResult was '${authResult}'`));
                    return;
                }
                if (authResult.expiresIn) {
                    scheduleRenewal(authResult.expiresIn);
                }
                resolve(authResult);
            };
            if (options) {
                webAuth.parseHash(options, callback);
            } else {
                webAuth.parseHash(callback);
            }
        });
    });
    return _parseHash.apply(this, arguments);
}
/**
 * Wraps WebAuth.client.userInfo.
 */ function userInfo(accessToken) {
    return _userInfo.apply(this, arguments);
}
function _userInfo() {
    _userInfo = swcHelpers.asyncToGenerator(function*(accessToken) {
        if (!accessToken) {
            throw new Error('accessToken needs to be non-empty');
        }
        return assertInit.rejectIfUninitialized((resolve, reject)=>{
            webAuth.client.userInfo(accessToken, (err, userResult)=>{
                if (err) {
                    reject(err);
                    return;
                }
                resolve(userResult);
            });
        });
    });
    return _userInfo.apply(this, arguments);
}
/**
 * Wraps WebAuth.logout, see: https://auth0.github.io/auth0.js/WebAuth.html#logout
 */ function logout(returnTo) {
    assertInit.throwIfUninitialized();
    window.clearTimeout(renewalTimeoutToken);
    // Redirects out
    webAuth.logout({
        returnTo
    });
}

export { authorize, checkSession, crossOriginAuthentication, init, logout, parseHash, renewalRequired, userInfo };
//# sourceMappingURL=auth.js.map
